import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "timetableSearchInputWrapper" }
const _hoisted_2 = { class: "from" }
const _hoisted_3 = ["id"]
const _hoisted_4 = ["value", "aria-expanded", "placeholder", "disabled"]
const _hoisted_5 = ["aria-label"]
const _hoisted_6 = ["id"]
const _hoisted_7 = ["value", "aria-expanded", "placeholder", "disabled"]

import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { directive as vClickOutside } from 'click-outside-vue3';
import { RecentOrSuggestedSearchEntry, SuggestionPortType } from '../../types';
import { mapRecentSearches, mapPortPairToRecentOrSuggestedSearchEntry } from '../../maps';
import { Icon, Card } from '../../Atoms';
import { RecentSearches } from '../../Molecules';
import { IconText } from '..';
import { useTimetableStore } from '../../store';


export default /*#__PURE__*/_defineComponent({
  __name: 'TimetableSearchInput',
  props: {
    from: {},
    fromCode: {},
    fromLabel: {},
    fromPlaceholder: {},
    to: {},
    toCode: {},
    toLabel: {},
    toPlaceholder: {},
    disabled: { type: Boolean },
    error: { type: Boolean },
    isDuplicateRoute: { type: Boolean }
  },
  emits: ['updateFrom', 'updateTo', 'updateFromCode', 'updateToCode', 'updateRouteId', 'updateRouteCode', 'searchClick'],
  setup(__props: any, { emit: __emit }) {

const { t } = useI18n();

const props = __props;

// Internal component state
const componentState = ref({
  switched: false,
  from: props.from ? props.from : '',
  fromCode: '',
  fromPlaceholder: props.fromPlaceholder,
  isFromFocus: false,
  to: props.to ? props.to : '',
  toCode: '',
  toPlaceholder: props.toPlaceholder,
  isToFocus: false,
  tempFrom: '',
  tempFromCode: '',
  tempTo: '',
  tempToCode: '',
  routeId: '',
  routeCode: '',
  showSearches: false,
  suggestions: [] as Array<RecentOrSuggestedSearchEntry>,
  suggestionsTitle: '',
  searchSelected: !!(props.from && props.to),
});

// Emittable events

const emit = __emit;

// Initialise the store

const timetableStore = useTimetableStore();

// Async functions for managing external state

const getSuggestions = async (
  portType: SuggestionPortType,
  newValue: string,
) => {
  componentState.value.suggestionsTitle = newValue.length < 3 ? t('Recent') : t('Select a route');
  componentState.value = { ...componentState.value, showSearches: true };
  if (newValue.length < 3) {
    const recent = timetableStore.recentSearches;
    const recentSuggestions = recent?.map((item) => mapRecentSearches(item));
    componentState.value = {
      ...componentState.value,
      showSearches: recentSuggestions !== undefined && recentSuggestions.length > 0,
    };
    if (recentSuggestions) {
      componentState.value.suggestions = recentSuggestions;
    }
  } else if (portType === SuggestionPortType.Origin) {
    try {
      await timetableStore.getSuggestionsByOriginPortName(newValue);
    } catch (e) {
      console.log('e', e);
    }
  } else if (portType === SuggestionPortType.Destination) {
    try {
      await timetableStore.getSuggestionsByDestinationPortName(newValue);
    } catch (e) {
      console.log('e', e);
    }
  }
};

const onOriginChanged = async (newValue: string) => {
  componentState.value = {
    ...componentState.value,
    from: newValue,
    searchSelected: !!(newValue && componentState.value.to),
  };
  await getSuggestions(SuggestionPortType.Origin, newValue);
};

const onDestinationChanged = async (newValue: string) => {
  componentState.value = {
    ...componentState.value,
    searchSelected: !!(newValue && componentState.value.from),
    to: newValue,
  };
  await getSuggestions(SuggestionPortType.Destination, newValue);
};

const switchLocations = () => {
  if (props.disabled) {
    return;
  }
  componentState.value.switched = !componentState.value.switched;
};

// Input event handlers

const updateEmits = () => {
  emit('updateFrom', componentState.value.from);
  emit('updateFromCode', componentState.value.fromCode);
  emit('updateTo', componentState.value.to);
  emit('updateToCode', componentState.value.toCode);
  emit('updateRouteId', componentState.value.routeId);
  emit('updateRouteCode', componentState.value.routeCode);
};

function handleFocus() {
  const focusComingFromPortSelection = componentState.value.isFromFocus
  || componentState.value.isToFocus;

  componentState.value = {
    ...componentState.value,
    searchSelected: false,
    tempFrom: focusComingFromPortSelection
      ? componentState.value.tempFrom
      : componentState.value.from,
    tempFromCode: focusComingFromPortSelection
      ? componentState.value.tempFromCode
      : componentState.value.fromCode,
    tempTo: focusComingFromPortSelection
      ? componentState.value.tempTo
      : componentState.value.to,
    tempToCode: focusComingFromPortSelection
      ? componentState.value.tempToCode
      : componentState.value.toCode,
    from: '',
    fromPlaceholder: '',
    to: '',
    toPlaceholder: '',
    routeId: '',
    routeCode: '',
  };
}

function handleBlur() {
  if (componentState.value.tempFrom !== '' || componentState.value.tempTo !== '') {
    componentState.value = {
      ...componentState.value,
      from: componentState.value.tempFrom,
      fromCode: componentState.value.tempFromCode,
      fromPlaceholder: props.fromPlaceholder,
      to: componentState.value.tempTo,
      toCode: componentState.value.tempToCode,
      toPlaceholder: props.toPlaceholder,
      searchSelected: !!(componentState.value.tempFromCode && componentState.value.tempToCode),
      tempFrom: '',
      tempFromCode: '',
      tempTo: '',
      tempToCode: '',
    };
  } else if (componentState.value.searchSelected === false) {
    componentState.value = {
      ...componentState.value,
      showSearches: false,
      from: '',
      fromCode: '',
      fromPlaceholder: props.fromPlaceholder,
      to: '',
      toCode: '',
      toPlaceholder: props.toPlaceholder,
      routeId: componentState.value.routeId,
      routeCode: componentState.value.routeCode,
    };
  } else {
    updateEmits();
    componentState.value = {
      ...componentState.value,
      showSearches: false,
    };
  }
  componentState.value.showSearches = false;
  componentState.value.isFromFocus = false;
  componentState.value.isToFocus = false;
  timetableStore.clearSuggestions();
}

const onOriginFocused = async () => {
  handleFocus();
  componentState.value.isFromFocus = true;
  await getSuggestions(SuggestionPortType.Origin, '');
};

const onDestinationFocused = async () => {
  handleFocus();
  componentState.value.isToFocus = true;
  await getSuggestions(SuggestionPortType.Destination, '');
};

// Recents list event handlers

const setSearchValue = (portCoupling: RecentOrSuggestedSearchEntry) => {
  componentState.value = {
    ...componentState.value,
    from: portCoupling.ports[0].name,
    fromCode: portCoupling.ports[0].portCode ?? '',
    to: portCoupling.ports[1].name,
    toCode: portCoupling.ports[1].portCode ?? '',
    tempFrom: '',
    tempFromCode: '',
    tempTo: '',
    tempToCode: '',
    routeId: portCoupling.routeId,
    routeCode: portCoupling.routeCode ? portCoupling.routeCode : '',
    searchSelected: true,
    isFromFocus: false,
    isToFocus: false,
    showSearches: false,
  };
  updateEmits();
};

watch(() => props.from, (from) => {
  if (from !== undefined) {
    componentState.value = {
      ...componentState.value,
      from,
    };
  }
});

watch(() => props.to, (to) => {
  if (to !== undefined) {
    componentState.value = {
      ...componentState.value,
      to,
    };
  }
});

watch(() => timetableStore.portPairSuggestions, (portPairs) => {
  if (portPairs && portPairs.length > 0) {
    const suggestions = portPairs?.map((route) => mapPortPairToRecentOrSuggestedSearchEntry(route));
    componentState.value.suggestions = suggestions;
  } else {
    componentState.value.suggestions = [];
  }
});

watch(() => componentState.value.switched, () => {
  const tempFrom = componentState.value.from;
  const tempFromCode = componentState.value.fromCode;
  const tempTo = componentState.value.to;
  const tempToCode = componentState.value.toCode;
  componentState.value.from = tempTo;
  componentState.value.fromCode = tempToCode;
  componentState.value.to = tempFrom;
  componentState.value.toCode = tempFromCode;
  updateEmits();
});


return (_ctx: any,_cache: any) => {
  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Card), {
      class: _normalizeClass([
        'timetableSearchInput',
        {
          disabled: _ctx.disabled,
          error: _ctx.error || _ctx.isDuplicateRoute,
          isFocus: (componentState.value.isFromFocus || componentState.value.isToFocus)
            && componentState.value.showSearches
        },
      ])
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_unref(IconText), {
            class: "placeholder",
            "icon-name": "searchMapPin",
            "icon-lg": true
          }, {
            default: _withCtx(() => [
              _createElementVNode("label", {
                id: _ctx.fromLabel,
                for: "from"
              }, _toDisplayString(_ctx.fromLabel), 9, _hoisted_3)
            ]),
            _: 1
          }),
          _createElementVNode("input", {
            id: "from",
            role: "combobox",
            value: componentState.value.from,
            type: "text",
            name: "from",
            "aria-controls": "timetableSuggestions",
            autocomplete: "off",
            "aria-haspopup": "listbox",
            "aria-labelledby": "fromLabel",
            "aria-expanded": componentState.value.showSearches,
            placeholder: componentState.value.fromPlaceholder,
            disabled: _ctx.disabled,
            onFocus: onOriginFocused,
            onInput: _cache[0] || (_cache[0] = ($event: any) => (onOriginChanged($event.target.value)))
          }, null, 40, _hoisted_4)
        ]),
        _createVNode(_unref(Card), {
          class: _normalizeClass(["switch", {fromFocus: componentState.value.isFromFocus && componentState.value.showSearches}])
        }, {
          default: _withCtx(() => [
            _createElementVNode("button", {
              "aria-label": _ctx.$t('Switch button aria label'),
              onClick: switchLocations
            }, [
              _createVNode(_unref(Icon), { "icon-name": "switch" })
            ], 8, _hoisted_5)
          ]),
          _: 1
        }, 8, ["class"]),
        _createElementVNode("div", {
          class: _normalizeClass(["to", {fromFocus: componentState.value.isFromFocus && componentState.value.showSearches}])
        }, [
          _createVNode(_unref(IconText), {
            class: "placeholder",
            "icon-name": "searchMapPin",
            "icon-lg": true
          }, {
            default: _withCtx(() => [
              _createElementVNode("label", {
                id: _ctx.toLabel,
                for: "to"
              }, _toDisplayString(_ctx.toLabel), 9, _hoisted_6)
            ]),
            _: 1
          }),
          _createElementVNode("input", {
            id: "to",
            role: "combobox",
            "aria-haspopup": "listbox",
            value: componentState.value.to,
            "aria-controls": "timetableSuggestions",
            "aria-labelledby": "toLabel",
            type: "text",
            name: "to",
            autocomplete: "off",
            "aria-expanded": componentState.value.showSearches,
            placeholder: componentState.value.toPlaceholder,
            disabled: _ctx.disabled,
            onFocus: onDestinationFocused,
            onInput: _cache[1] || (_cache[1] = ($event: any) => (onDestinationChanged($event.target.value)))
          }, null, 40, _hoisted_7)
        ], 2)
      ]),
      _: 1
    }, 8, ["class"]),
    _createVNode(_unref(RecentSearches), {
      class: _normalizeClass({
        isFocus: (componentState.value.isFromFocus || componentState.value.isToFocus)
          && componentState.value.showSearches
      }),
      title: componentState.value.suggestionsTitle,
      "show-searches": componentState.value.showSearches,
      searches: componentState.value.suggestions,
      "aria-id": "timetableSuggestions",
      onSetSearchValue: _cache[2] || (_cache[2] = (value) => setSearchValue(value))
    }, null, 8, ["class", "title", "show-searches", "searches"])
  ])), [
    [_unref(vClickOutside), handleBlur]
  ])
}
}

})